// src/pages/user/EnhancedUserOfferings.tsx
import React, { useState, useEffect, useRef, memo } from "react";
import { useNavigate } from "react-router-dom";
import Hls from "hls.js";
import UserDashboardLayout from "../../components/layout/UserDashboardLayout";
import Alert from "../../components/common/alert";
import { getAllPackages, getPurchaseData, } from "../../api/packageService";
import { getAllCourses, getCoursePriceByCourseId } from "../../api/courseService";
import {
  Search,
  Gift,
  Tag,
  Clock,
  Star,
  ShoppingCart,
  X,
  Lock,
  User as UserIcon,
  Mail,
  Phone,
  Package as PackageIcon,
  BookOpen,
  Video,
} from "lucide-react";
import '../../style/enhanced-user-offerings.css';
import { FRONTEND_URL } from "api/apiService";

// Package interface with proper fields
interface Package {
  _id: string;
  title: string;
  description: string;
  bannerBase64?: string;
  bannerUrl?: string;
  themeBase64?: string;
  themeUrl?: string;
  createdAt: string;
  isDraft: boolean;
  status: string;
  isActive: boolean;
  priceDetails: Array<{
    period: string;
    actualPrice: number;
    price: number;
    gst: number;
    totalprice: number;
  }>;
  faqs: Array<{
    question: string;
    answer: string;
  }>;
  type: 'package';
}

// Course interface
interface Course {
  _id: string;
  title: string;
  description: string;
  thumbnail: string;
  createdAt: string;
  language: string;
  desktopAvailable: boolean;
  PublishStatus: string;
  courseType: string;
  webinarChannel: string | null;
  type: 'course';
  priceDetails?: Array<{
    _id: string;
    price: number;
    actualPrice: number;
    validityType: string;
    validityInDays: number;
    gstRateId: {
      _id: string;
      gstPercent: number;
    };
  }>;
}

// User data interface
interface UserData {
  name?: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  _id?: string;
  usertypeId?: string;
}

// Union type for offerings
type Offering = Package | Course;

// Purchase form data
interface PurchaseFormData {
  userId: string;
  name: string;
  email: string;
  phone: string;
  packageId?: string;
  courses: Array<{
    courseId: string;
    courseViewPercent: number;
  }>;
}

const EnhancedUserOfferings: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [filteredOfferings, setFilteredOfferings] = useState<Offering[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<{
    type: "success" | "error" | "info" | "warning";
    message: string;
  } | null>(null);
  const [previewPopup, setPreviewPopup] = useState<{
    isOpen: boolean;
    offering: Offering | null;
  }>({
    isOpen: false,
    offering: null,
  });
  const [iframeModal, setIframeModal] = useState<{
    isOpen: boolean;
    url: string | null;
  }>({ isOpen: false, url: null });
  const [purchaseForm, setPurchaseForm] = useState<{
    isOpen: boolean;
    offering: Offering | null;
    formData: PurchaseFormData;
  }>({
    isOpen: false,
    offering: null,
    formData: {
      userId: "",
      name: "",
      email: "",
      phone: "",
      courses: []
    }
  });
  const [userData, setUserData] = useState<UserData | null>(null);
  const [purchasedOfferings, setPurchasedOfferings] = useState<Set<string>>(new Set());
  
  // Refs for video elements to prevent re-rendering issues
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  
  // Store video URLs to prevent re-rendering
  const videoUrls = useRef<Record<string, string>>({});
  
  // Function to update video source safely
  // const updateVideoSource = useCallback((videoId: string, url: string) => {
  //   if (!videoRefs.current[videoId] || videoUrls.current[videoId] === url) {
  //     return;
  //   }
    
  //   videoUrls.current[videoId] = url;
  //   if (videoRefs.current[videoId]) {
  //     videoRefs.current[videoId]!.src = url;
  //   }
  // }, []);

  // Cleanup video elements on unmount
  useEffect(() => {
    return () => {
      // Pause all videos and clean up
      Object.values(videoRefs.current).forEach(video => {
        if (video) {
          video.pause();
        }
      });
    };
  }, []);

  // Cleanup video elements when offerings change
  useEffect(() => {
    // Create a set of current offering IDs
    const currentOfferingIds = new Set(offerings.map(o => o._id));
    
    // Clean up video refs for offerings that no longer exist
    Object.keys(videoRefs.current).forEach(key => {
      // Check if this is a popup video (starts with popup-)
      if (key.startsWith('popup-')) {
        // For popup videos, only clean up if popup is not open or offering changed
        if (!previewPopup.isOpen || !previewPopup.offering || key !== `popup-${previewPopup.offering._id}`) {
          const video = videoRefs.current[key];
          if (video) {
            video.pause();
            delete videoRefs.current[key];
            delete videoUrls.current[key];
          }
        }
      } else {
        // For regular offering videos, clean up if offering no longer exists
        if (!currentOfferingIds.has(key)) {
          const video = videoRefs.current[key];
          if (video) {
            video.pause();
            delete videoRefs.current[key];
            delete videoUrls.current[key];
          }
        }
      }
    });
  }, [offerings, previewPopup]);

  // Additional cleanup when popup closes
  useEffect(() => {
    if (!previewPopup.isOpen && previewPopup.offering) {
      const popupId = `popup-${previewPopup.offering._id}`;
      const video = videoRefs.current[popupId];
      if (video) {
        video.pause();
        delete videoRefs.current[popupId];
        delete videoUrls.current[popupId];
      }
    }
  }, [previewPopup.isOpen, previewPopup.offering]);

  // Fetch purchased offerings for the current user
  const fetchPurchasedOfferings = async (userId: string) => {
    try {
      const purchaseResponse = await getPurchaseData(userId);
      if (purchaseResponse.status === 200 && purchaseResponse.data) {
        const purchasedIds = new Set<string>();
        
        // Add all purchased course IDs to the set
        purchaseResponse.data.purchases.forEach(purchase => {
          purchase.courses.forEach(course => {
            purchasedIds.add(course.courseId);
          });
          
          // Add package IDs if they exist
          if (purchase.packageId) {
            purchasedIds.add(purchase.packageId);
          }
        });
        
        setPurchasedOfferings(purchasedIds);
      }
    } catch (error) {
      console.error("Error fetching purchased offerings:", error);
    }
  };

  // Fetch offerings and user data
  useEffect(() => {
    const fetchOfferings = async () => {
      try {
        setLoading(true);
        
        // Get user data from localStorage
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          try {
            const parsedData = JSON.parse(storedUserData);
            setUserData(parsedData);
            
            // Set default form data
            const fullName = parsedData.name || `${parsedData.firstName || ''} ${parsedData.lastName || ''}`.trim();
            setPurchaseForm(prev => ({
              ...prev,
              formData: {
                ...prev.formData,
                userId: parsedData._id || "",
                name: fullName,
                email: parsedData.email || "",
                phone: parsedData.phone || ""
              }
            }));
            
            // Fetch purchased offerings for this user
            if (parsedData._id) {
              await fetchPurchasedOfferings(parsedData._id);
            }
          } catch (error) {
            console.error('Error parsing user data:', error);
          }
        }
        
        // Fetch packages - only published and sold out, not draft
        const packagesResponse = await getAllPackages();
        let allOfferings: Offering[] = [];
        
        if (packagesResponse.status === 200 && packagesResponse.data) {
          // Add type to packages and filter out drafts
          const packagesWithType: Offering[] = packagesResponse.data
            .filter((pkg: any) => !pkg.isDraft && (pkg.status === "Published" || pkg.status === "Sold out"))
            .map((pkg: any) => ({
              ...pkg,
              type: 'package'
            }));
        allOfferings = [...packagesWithType];
        }
        
        // Fetch courses - only published and sold out
        const coursesResponse = await getAllCourses();
        
        if (coursesResponse.status === 200 && coursesResponse.data) {
          // Add type to courses and filter by PublishStatus
          const coursesWithType: Offering[] = await Promise.all(
            coursesResponse.data
              .filter((course: any) => course.PublishStatus === "Published" || course.PublishStatus === "Sold out")
              .map(async (course: any) => {
                // Fetch course price details
                const priceResponse = await getCoursePriceByCourseId(course._id);
                let priceDetails = undefined;
                
                if (priceResponse.status === 200 && priceResponse.data && priceResponse.data.priceDetail) {
                  priceDetails = priceResponse.data.priceDetail;
                }
                
                return {
                  ...course,
                  type: 'course',
                  priceDetails
                };
              })
          );
        allOfferings = [...allOfferings, ...coursesWithType];
        }
        
        setOfferings(allOfferings);
        setFilteredOfferings(allOfferings);
      } catch (error) {
        setAlert({
          type: "error",
          message: "Please check your authentication and try again",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchOfferings();
    
    // Also listen for storage changes in case user data is updated elsewhere
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userData') {
        if (e.newValue) {
          try {
            const parsedData = JSON.parse(e.newValue);
            setUserData(parsedData);
            
            // Update form data
            const fullName = parsedData.name || `${parsedData.firstName || ''} ${parsedData.lastName || ''}`.trim();
            setPurchaseForm(prev => ({
              ...prev,
              formData: {
                ...prev.formData,
                userId: parsedData._id || "",
                name: fullName,
                email: parsedData.email || "",
                phone: parsedData.phone || ""
              }
            }));
            
            // Fetch purchased offerings for this user
            if (parsedData._id) {
              fetchPurchasedOfferings(parsedData._id);
            }
          } catch (error) {
            console.error('Error parsing updated user data:', error);
          }
        }
      } else if (e.key === 'purchaseUpdated') {
        // Refresh purchased offerings when a new purchase is made
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          try {
            const parsedData = JSON.parse(storedUserData);
            if (parsedData._id) {
              fetchPurchasedOfferings(parsedData._id);
            }
          } catch (error) {
            console.error('Error parsing user data for purchase update:', error);
          }
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Filter offerings based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredOfferings(offerings);
    } else {
      const filtered = offerings.filter(offering => 
        offering.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offering.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOfferings(filtered);
    }
  }, [searchTerm, offerings]);

  // Get offering thumbnail with proper handling
  const getOfferingThumbnail = (offering: Offering) => {
    if (offering.type === 'package') {
      const pkg = offering as Package;
      // Handle base64 images first
      if (pkg.bannerBase64) {
        const cleanBase64 = pkg.bannerBase64.replace(/^data:image\/[a-z]+;base64,/, '');
        return `data:image/jpeg;base64,${cleanBase64}`;
      }
      
      // Handle URL images with token
      if (pkg.bannerUrl) {
        // Check if token is already present in the URL
        if (pkg.bannerUrl.includes('token=')) {
          // console.log('Token already present in bannerUrl:', pkg.bannerUrl);
          return pkg.bannerUrl;
        }
        
        const authToken = localStorage.getItem("AUTH_TOKEN");
        if (authToken) {
          const separator = pkg.bannerUrl.includes('?') ? '&' : '?';
          const urlWithToken = `${pkg.bannerUrl}${separator}token=${encodeURIComponent(authToken)}`;
          // console.log('Adding token to bannerUrl:', urlWithToken);
          return urlWithToken;
        }
        return pkg.bannerUrl;
      }
      
      // Fallback to theme images
      if (pkg.themeBase64) {
        const cleanBase64 = pkg.themeBase64.replace(/^data:image\/[a-z]+;base64,/, '');
        return `data:image/jpeg;base64,${cleanBase64}`;
      }
      
      if (pkg.themeUrl) {
        // Check if token is already present in the URL
        if (pkg.themeUrl.includes('token=')) {
          // console.log('Token already present in themeUrl:', pkg.themeUrl);
          return pkg.themeUrl;
        }
        
        const authToken = localStorage.getItem("AUTH_TOKEN");
        if (authToken) {
          const separator = pkg.themeUrl.includes('?') ? '&' : '?';
          const urlWithToken = `${pkg.themeUrl}${separator}token=${encodeURIComponent(authToken)}`;
          // console.log('Adding token to themeUrl:', urlWithToken);
          return urlWithToken;
        }
        return pkg.themeUrl;
      }
    } else {
      // Handle course thumbnail
      const course = offering as Course;
      if (course.thumbnail) {
        // Check if it's already a data URL
        if (course.thumbnail.startsWith('data:')) {
          return course.thumbnail;
        }
        
        // Check if token is already present in the URL
        if (course.thumbnail.includes('token=')) {
          // console.log('Token already present in thumbnail:', course.thumbnail);
          return course.thumbnail;
        }
        
        // Check if it's a streaming URL
        if (course.thumbnail.includes('/stream/')) {
          // For streaming URLs, we might need to append the token differently
          const authToken = localStorage.getItem("AUTH_TOKEN");
          if (authToken) {
            // For streaming URLs, the token is usually part of the URL path, not a query parameter
            // But we'll still append it as a query parameter for consistency
            const separator = course.thumbnail.includes('?') ? '&' : '?';
            const urlWithToken = `${course.thumbnail}${separator}token=${encodeURIComponent(authToken)}`;
            // console.log('Adding token to streaming thumbnail:', urlWithToken);
            return urlWithToken;
          }
          return course.thumbnail;
        }
        
        const authToken = localStorage.getItem("AUTH_TOKEN");
        if (authToken) {
          const separator = course.thumbnail.includes('?') ? '&' : '?';
          const urlWithToken = `${course.thumbnail}${separator}token=${encodeURIComponent(authToken)}`;
          // console.log('Adding token to thumbnail:', urlWithToken);
          return urlWithToken;
        }
        return course.thumbnail;
      }
    }
    
    // Default fallback
    return "/placeholder.svg";
  };

  // Handle offering preview click
  const handlePreviewClick = (offering: Offering) => {
    // For sold out items, show a message instead of allowing preview
    const status = getOfferingStatus(offering);
    if (status.toLowerCase() === 'sold out') {
      setAlert({
        type: "info",
        message: `"${offering.title}" is currently sold out and not available for purchase.`,
      });
      return;
    }
    
    setPreviewPopup({
      isOpen: true,
      offering: offering
    });
  };

  const openCourseInModal = (offering: Offering) => {
    if (offering.type == 'course'){

    const url = `${FRONTEND_URL}/course/${offering._id}?t=${localStorage.getItem('AUTH_TOKEN') || ''}`;
    window.open(url, '_self');
    // setIframeModal({ isOpen: true, url });
    } else if (offering.type == 'package'){
      const url = `${FRONTEND_URL}/packages/${offering._id}?t=${localStorage.getItem('AUTH_TOKEN') || ''}`;
       window.open(url, '_self');
    }
  };

  const closeIframeModal = () => {
    setIframeModal({ isOpen: false, url: null });
    window.location.reload();
  };

  // Handle buy now click
  const handleBuyNowClick = (offering: Offering) => {
    // For sold out items, show a message instead of allowing purchase
    const status = getOfferingStatus(offering);
    if (status.toLowerCase() === 'sold out') {
      setAlert({
        type: "info",
        message: `"${offering.title}" is currently sold out and not available for purchase.`,
      });
      return;
    }
    
    // Set up purchase form with offering data
    // const formData: PurchaseFormData = {
    //   userId: userData?._id || "",
    //   name: userData?.name || `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim(),
    //   email: userData?.email || "",
    //   phone: userData?.phone || "",
    //   courses: offering.type === 'course' ? 
    //     [{ courseId: offering._id, courseViewPercent: 0 }] : 
    //     [],
    //   packageId: offering.type === 'package' ? offering._id : undefined
    // };
    
    // setPurchaseForm({
    //   isOpen: true,
    //   offering: offering,
    //   formData: formData
    // });
    openCourseInModal(offering)
  };

  // Close preview popup
  const closePreviewPopup = () => {
    setPreviewPopup({
      isOpen: false,
      offering: null
    });
  };

  // Close purchase form
  const closePurchaseForm = () => {
    setPurchaseForm({
      isOpen: false,
      offering: null,
      formData: {
        userId: "",
        name: "",
        email: "",
        phone: "",
        courses: []
      }
    });
  };

  // Handle form input changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPurchaseForm(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [name]: value
      }
    }));
  };

  // Handle form submission
  // const handleFormSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault();
    
  //   try {
  //     // Prepare purchase data based on offering type
  //     const offering = purchaseForm.offering;
  //     if (!offering) {
  //       throw new Error("No offering selected for purchase");
  //     }
      
  //     const purchaseData: any = {
  //       userId: purchaseForm.formData.userId,
  //       name: purchaseForm.formData.name,
  //       email: purchaseForm.formData.email,
  //       phone: purchaseForm.formData.phone,
  //     };
      
  //     // Add packageId or courses based on offering type
  //     if (offering.type === 'package') {
  //       purchaseData.packageId = offering._id;
  //       // For packages, we need to include all courses in the package
  //       // First, fetch the package details to get course IDs
  //       const packageResponse = await getPackageById(offering._id);
  //       if (packageResponse.status === 200 && packageResponse.data) {
  //         // Create courses array with all course IDs from the package
  //         purchaseData.courses = packageResponse.data.courseIds.map((courseId: string) => ({
  //           courseId: courseId,
  //           courseViewPercent: 0.0
  //         }));
  //       } 
  //       // Note: We don't add courses field if we can't fetch package details
  //       // The backend should handle package purchases without explicit courses
  //     } else {
  //       purchaseData.courses = [
  //         { 
  //           courseId: offering._id, 
  //           courseViewPercent: 0 
  //         }
  //       ];
  //     }
      
  //     // Call the appropriate API
  //     const response = await createPurchase(purchaseData);
      
  //     // For single course purchases, also initialize course progress
  //     if (response.status === 200 && offering.type === 'course') {
  //       // Get course price details to determine expiry
  //       const priceResponse = await getCoursePriceByCourseId(offering._id);
  //       let expiry = "90"; // default value (90 days as string)
        
  //       if (priceResponse.status === 200 && priceResponse.data && priceResponse.data.priceDetail) {
  //         // Use the first price detail's validity in days as expiry
  //         const priceDetail = priceResponse.data.priceDetail[0];
  //         if (priceDetail && priceDetail.validityInDays) {
  //           expiry = priceDetail.validityInDays.toString(); // Convert number to string
  //         }
  //       }
        
  //       // Initialize course progress
  //       await initializeCourseProgress(purchaseForm.formData.userId, offering._id, expiry);
  //     }
  //     // For package purchases, also initialize package progress
  //     else if (response.status === 200 && offering.type === 'package') {
  //       // Initialize package progress
  //       await initializePackageProgress(purchaseForm.formData.userId, offering._id);
  //     }
      
  //     if (response.status === 200) {
  //       const offeringType = offering.type === 'package' ? 'Package' : 'Course';
  //       const article = offeringType === 'Package' ? 'a' : 'a';
  //       setAlert({
  //         type: "success",
  //         message: `You have successfully purchased ${article} ${offeringType.toLowerCase()} "${offering.title}"!`,
  //       });
        
  //       // Notify other components that a purchase was made
  //       localStorage.setItem('purchaseUpdated', Date.now().toString());
  //       window.dispatchEvent(new Event('storage'));
        
  //       // Refresh purchased offerings for this user
  //       if (userData?._id) {
  //         await fetchPurchasedOfferings(userData._id);
  //       }
        
  //       // Close the purchase form and redirect to purchased courses page
  //       closePurchaseForm();
  //       navigate('/UserPurchases'); // Redirect to purchased courses page with correct path
  //     } else {
  //       throw new Error(response.message || "Failed to create purchase");
  //     }
  //   } catch (error: any) {
  //     console.error("Purchase error:", error);
  //     setAlert({
  //       type: "error",
  //       message: error.message || "Failed to submit purchase request. Please try again.",
  //     });
  //   }
  // };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const offering = purchaseForm.offering;
    if (!offering) {
      setAlert({ type: 'error', message: 'No offering selected' });
      return;
    }

    const token = localStorage.getItem('AUTH_TOKEN') || '';
    const segment = offering.type === 'package' ? 'packages' : 'course';
    const url = `${FRONTEND_URL}/${segment}/${offering._id}?t=${token}`;
    console.log('url::: ', url);
    setIframeModal({ isOpen: true, url });
    closePurchaseForm();
  };


  // Format price period
  const formatPeriod = (period: string) => {
    switch (period) {
      case '1month': return '1 Month';
      case '3months': return '3 Months';
      case '6months': return '6 Months';
      case '1year': return '1 Year';
      default: return period;
    }
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'sold out': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get offering status
  const getOfferingStatus = (offering: Offering) => {
    if (offering.type === 'package') {
      return (offering as Package).status;
    } else {
      return (offering as Course).PublishStatus;
    }
  };

  // Format course price period
  const formatCoursePeriod = (validityType: string, validityInDays: number) => {
    if (validityType === 'Lifetime') return 'Lifetime';
    if (validityInDays === 30) return '1 Month';
    if (validityInDays === 90) return '3 Months';
    if (validityInDays === 180) return '6 Months';
    if (validityInDays === 365) return '1 Year';
    return `${validityInDays} days`;
  };

  // Helper function to determine if a URL is a video
  const isVideoUrl = (url: string): boolean => {
    // Check for data URLs - only video data URLs
    if (url.startsWith('data:video')) {
      // console.log('Video URL detected (data:video):', url);
      return true;
    }
    
    // Check for streaming URLs
    if (url.includes('/stream/')) {
      // console.log('Video URL detected (/stream/):', url);
      return true;
    }
    
    // Check for file extensions in the URL
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    const isVideo = videoExtensions.some(ext => url.includes(ext));
    // if (isVideo) {
    //   console.log('Video URL detected (extension):', url);
    // }
    return isVideo;
  };

  // Dedicated Video Component with optimized rendering
  const OptimizedVideoThumbnail = memo(({ src, alt }: { src: string; alt: string }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    
    useEffect(() => {
      if (!videoRef.current) return;
      
      // Reset states
      setIsLoading(true);
      setHasError(false);
      
      // Clean up previous HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      
      const videoElement = videoRef.current;
      
      // Handle video events
      const handleLoadedData = () => {
        setIsLoading(false);
      };
      
      const handleError = (e: any) => {
        console.error("Video error for:", src, e);
        setIsLoading(false);
        setHasError(true);
        // Fallback to placeholder
        if (videoElement) {
          videoElement.src = "/placeholder.svg";
        }
      };
      
      videoElement.addEventListener('loadeddata', handleLoadedData);
      videoElement.addEventListener('error', handleError);
      
      // Check if this is a streaming URL that needs HLS
      if (src.includes('/stream/') || src.includes('.m3u8')) {
        if (Hls.isSupported()) {
          // Initialize HLS player
          const hls = new Hls({
            enableWorker: true,
          });
          
          hls.loadSource(src);
          hls.attachMedia(videoElement);
          
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log("HLS manifest parsed successfully for:", src);
            setIsLoading(false);
          });
          
          hls.on(Hls.Events.ERROR, (event, data) => {
            console.error("HLS error for:", src, data);
            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  hls.startLoad();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  hls.recoverMediaError();
                  break;
                default:
                  hls.destroy();
                  setHasError(true);
                  setIsLoading(false);
                  break;
              }
            }
          });
          
          hlsRef.current = hls;
        } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
          // For Safari
          videoElement.src = src;
          // Try to play after setting src
          videoElement.play().catch(e => {
            console.log("Autoplay prevented:", e);
          });
        } else {
          // Fallback for browsers that don't support HLS
          videoElement.src = src;
        }
      } else {
        // For regular video files
        videoElement.src = src;
      }
      
      // Cleanup
      return () => {
        videoElement.removeEventListener('loadeddata', handleLoadedData);
        videoElement.removeEventListener('error', handleError);
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    }, [src]);
    
    return (
      <div className="relative w-full h-full">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}
        {hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
            <div className="text-white text-sm">Failed to load video</div>
          </div>
        )}
        <video
          ref={videoRef}
          controls
          className="w-full h-full object-cover transition-opacity duration-300"
          style={{ backgroundColor: 'black', opacity: isLoading || hasError ? 0 : 1 }}
          onError={(e) => {
            console.error("Video element error:", e);
            setHasError(true);
            setIsLoading(false);
          }}
        />
      </div>
    );
  });

  // Dedicated Image Component with optimized rendering
  const OptimizedImageThumbnail = memo(({ src, alt }: { src: string; alt: string }) => {
    return (
      <img
        src={src}
        alt={alt}
        className="package-image"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = "/placeholder.svg";
        }}
      />
    );
  });

  return (
    <UserDashboardLayout>
      <div className="enhanced-user-offerings">
        {alert && (
          <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        )}
        
        {/* Header Section */}
        <div className="offerings-header">
          <div className="header-content">
            <h1 className="header-title">  <Gift size={24} color="#4f46e5" /> Exclusive Learning Offerings</h1>
            <p className="header-subtitle">Discover our carefully curated learning packages and courses designed to accelerate your growth</p>
          </div>
          
          {/* Search Bar - Improved styling to match UserPurchases */}
          <div className="search-container">
            <div className="search-wrapper">
              <div className="w-full md:w-64">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <Search className="h-3.5 w-3.5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search offerings..."
                    className="block w-full pl-7 pr-3 py-1.5 text-md border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-icon bg-blue-100 text-blue-600">
              <Gift size={20} />
            </div>
            <div className="stat-info">
              <p className="stat-value">{offerings.length}</p>
              <p className="stat-label">Total Offerings</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon bg-green-100 text-green-600">
              <Tag size={20} />
            </div>
            <div className="stat-info">
              <p className="stat-value">
                {offerings.filter(o => getOfferingStatus(o).toLowerCase() === 'published').length}
              </p>
              <p className="stat-label">Available Now</p>
            </div>
          </div>
          
      
        </div>

        {/* Offerings Grid */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading offerings...</p>
          </div>
        ) : filteredOfferings.length === 0 ? (
          <div className="empty-state">
            <Gift className="empty-icon" size={48} />
            <h3 className="empty-title">No Offerings Found</h3>
            <p className="empty-description">
              {searchTerm ? 
                `No offerings match your search for "${searchTerm}". Try different keywords.` :
                "We don't have any offerings available at the moment. Check back soon!"
              }
            </p>
          </div>
        ) : (
          <div className="packages-grid">
            {filteredOfferings.map((offering) => {
              const status = getOfferingStatus(offering);
              const isSoldOut = status.toLowerCase() === 'sold out';
              
              return (
                <div 
                  key={offering._id} 
                  className={`package-card ${isSoldOut ? 'sold-out' : ''}`}
                >
                  <div className="package-image-container">
                    {(() => {
                      const thumbnailUrl = getOfferingThumbnail(offering);
                      const isVideo = isVideoUrl(thumbnailUrl);
                      // console.log('Rendering thumbnail for offering:', offering._id, 'URL:', thumbnailUrl, 'IsVideo:', isVideo);
                      
                      if (isVideo) {
                        return (
                          <OptimizedVideoThumbnail 
                            src={thumbnailUrl} 
                            alt={offering.title} 
                          />
                        );
                      } else {
                        return (
                          <OptimizedImageThumbnail 
                            src={thumbnailUrl} 
                            alt={offering.title} 
                          />
                        );
                      }
                    })()}
                    <div className="package-overlay">
                      <button 
                        className="preview-button"
                        onClick={() => handlePreviewClick(offering)}
                      >
                        <Lock size={14} />
                        {isSoldOut ? 'Sold Out' : 'Preview'}
                      </button>
                    </div>
                    <div className={`package-status ${getStatusColor(status)}`}>
                      {status}
                    </div>
                    <div className="offering-type-badge">
                      {offering.type === 'package' ? (
                        <span className="package-badge" style={{ color: '#7e22ce', backgroundColor: '#f8fafc' }}><PackageIcon size={12} /> Package</span>
                      ) : (offering as Course).courseType === 'Webinar' ? (
                        <span className="webinar-badge" style={{ color: '#c2410c', backgroundColor: '#f8fafc' }}><Video size={12} /> Webinar</span>
                      ) : (
                        <span className="course-badge" style={{ color: '#0369a1', backgroundColor: '#f8fafc' }}><BookOpen size={12} /> Course</span>
                      )}
                    </div>
                    {isSoldOut && (
                      <div className="sold-out-badge">
                        SOLD OUT
                      </div>
                    )}
                  </div>
                  
                  <div className="package-content">
                    <h3 className="package-title">{offering.title}</h3>
                    <p className="package-description">{offering.description}</p>
                    
                    <div className="package-meta">
                      <div className="meta-item">
                        <Clock size={14} />
                        <span>{offering.createdAt ? new Date(offering.createdAt).toLocaleDateString() : 'N/A'}</span>
                      </div>
                      <div className="meta-item">
                        <Star size={14} />
                        <span>4.8 (128 reviews)</span>
                      </div>
                    </div>
                    
                    {/* Price Section for Packages */}
                    {offering.type === 'package' && (offering as Package).priceDetails && (offering as Package).priceDetails.length > 0 && !isSoldOut && (
                      <div className="price-section">
                        <div className="price-info">
                          <span className="current-price">₹{(offering as Package).priceDetails[0].price}</span>
                          {(offering as Package).priceDetails[0].actualPrice > (offering as Package).priceDetails[0].price && (
                            <span className="original-price">₹{(offering as Package).priceDetails[0].actualPrice}</span>
                          )}
                        </div>
                        <div className="price-period">
                          {formatPeriod((offering as Package).priceDetails[0].period)}
                        </div>
                      </div>
                    )}
                    
                    {/* Price Section for Courses */}
                    {offering.type === 'course' && (offering as Course).priceDetails && (offering as Course).priceDetails!.length > 0 && !isSoldOut && (
                      <div className="price-section">
                        <div className="price-info">
                          <span className="current-price">₹{(offering as Course).priceDetails![0].price}</span>
                          {(offering as Course).priceDetails![0].actualPrice > (offering as Course).priceDetails![0].price && (
                            <span className="original-price">₹{(offering as Course).priceDetails![0].actualPrice}</span>
                          )}
                        </div>
                        <div className="price-period">
                          {formatCoursePeriod(
                            (offering as Course).priceDetails![0].validityType,
                            (offering as Course).priceDetails![0].validityInDays
                          )}
                        </div>
                      </div>
                    )}
                    
                    {offering.type === 'course' && (offering as Course).courseType === 'Webinar' ? (
                      <button
                        className="buy-button"
                        onClick={() => navigate(`/video/${offering._id}`)}
                      >
                        <Video size={16} />
                        Watch Now
                      </button>
                    ) : (
                      <button
                        className="buy-button"
                        onClick={() => handleBuyNowClick(offering)}
                      >
                        <ShoppingCart size={16} />
                        {isSoldOut ? 'Sold Out' : purchasedOfferings.has(offering._id) ? 'Already Purchased' : 'Buy Now'}
                      </button>
                    )}
                    {/* {offering.type === 'course' && !isSoldOut && (
                      <button
                        className="ml-2 text-sm text-blue-600 underline"
                        onClick={() => openCourseInModal(offering)}
                      >
                        Open Course
                      </button>
                    )} */}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Preview Popup */}
        {previewPopup.isOpen && previewPopup.offering && (
          <div className="popup-overlay">
            <div className="popup-container">
              <button 
                onClick={closePreviewPopup}
                className="popup-close"
              >
                <X size={24} />
              </button>
              
              <div className="popup-header">
                <h3 className="popup-title">
                  {getOfferingStatus(previewPopup.offering) === "Sold out" 
                    ? (previewPopup.offering.type === 'package' ? "📦 Package Sold Out" : "📚 Course Sold Out")
                    : (previewPopup.offering.type === 'package' ? "🔒 Preview Package" : "🔒 Preview Course")}
                </h3>
              </div>
              
              <p className="popup-description">
                {getOfferingStatus(previewPopup.offering) === "Sold out" 
                  ? `"${previewPopup.offering.title}" is currently sold out and not available for purchase.`
                  : `You need to purchase "${previewPopup.offering.title}" to access the full content. Click "Buy ${previewPopup.offering.type === 'package' ? 'Package' : 'Course'}" to get immediate access.`}
              </p>
              
              <div className="popup-image-container">
                {(() => {
                  if (!previewPopup.offering) return null;
                  
                  const thumbnailUrl = getOfferingThumbnail(previewPopup.offering);
                  const isVideo = isVideoUrl(thumbnailUrl);
                  // console.log('Rendering popup thumbnail for offering:', previewPopup.offering._id, 'URL:', thumbnailUrl, 'IsVideo:', isVideo);
                  
                  if (isVideo) {
                    return (
                      <OptimizedVideoThumbnail 
                        src={thumbnailUrl} 
                        alt={previewPopup.offering.title} 
                      />
                    );
                  } else {
                    return (
                      <OptimizedImageThumbnail 
                        src={thumbnailUrl} 
                        alt={previewPopup.offering.title} 
                      />
                    );
                  }
                })()}
                {getOfferingStatus(previewPopup.offering) === "Sold out" && (
                  <div className="sold-out-overlay">
                    <span className="sold-out-text">SOLD OUT</span>
                  </div>
                )}
              </div>
              
              {/* Price - only show for non-sold out items */}
              {getOfferingStatus(previewPopup.offering) !== "Sold out" && (
                <div className="popup-price-section">
                  <div className="popup-price-row">
                    <span className="price-label">
                      {previewPopup.offering.type === 'package' ? 'Package Price:' : 'Course Price:'}
                    </span>
                    {previewPopup.offering.type === 'package' 
                      ? ((previewPopup.offering as Package).priceDetails && (previewPopup.offering as Package).priceDetails.length > 0 ? (
                          <div className="price-values">
                            <span className="current-price-large">₹{(previewPopup.offering as Package).priceDetails[0].price}</span>
                            {(previewPopup.offering as Package).priceDetails[0].actualPrice > (previewPopup.offering as Package).priceDetails[0].price && (
                              <span className="original-price-large">₹{(previewPopup.offering as Package).priceDetails[0].actualPrice}</span>
                            )}
                          </div>
                        ) : (
                          <span className="price-unavailable">Price not available</span>
                        ))
                      : ((previewPopup.offering as Course).priceDetails && (previewPopup.offering as Course).priceDetails!.length > 0 ? (
                          <div className="price-values">
                            <span className="current-price-large">₹{(previewPopup.offering as Course).priceDetails![0].price}</span>
                            {(previewPopup.offering as Course).priceDetails![0].actualPrice > (previewPopup.offering as Course).priceDetails![0].price && (
                              <span className="original-price-large">₹{(previewPopup.offering as Course).priceDetails![0].actualPrice}</span>
                            )}
                          </div>
                        ) : (
                          <span className="price-unavailable">Price not available</span>
                        ))
                    }
                  </div>
                  
                  {previewPopup.offering.type === 'package' && 
                   (previewPopup.offering as Package).priceDetails && 
                   (previewPopup.offering as Package).priceDetails.length > 0 && 
                   (previewPopup.offering as Package).priceDetails[0].gst > 0 && (
                    <div className="gst-info">
                      <span>GST ({(previewPopup.offering as Package).priceDetails[0].gst}%):</span>
                      <span>₹{(previewPopup.offering as Package).priceDetails[0].price * (previewPopup.offering as Package).priceDetails[0].gst / 100}</span>
                    </div>
                  )}
                  
                  {previewPopup.offering.type === 'course' && 
                   (previewPopup.offering as Course).priceDetails && 
                   (previewPopup.offering as Course).priceDetails!.length > 0 && 
                   (previewPopup.offering as Course).priceDetails![0].gstRateId && (
                    <div className="gst-info">
                      <span>GST ({(previewPopup.offering as Course).priceDetails![0].gstRateId.gstPercent}%):</span>
                      <span>₹{Math.round((previewPopup.offering as Course).priceDetails![0].price * (previewPopup.offering as Course).priceDetails![0].gstRateId.gstPercent / 100)}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="popup-actions">
                <button 
                  className="cancel-button"
                  onClick={closePreviewPopup}
                >
                  Cancel
                </button>
                {previewPopup.offering?.type === 'course' && (previewPopup.offering as Course).courseType === 'Webinar' ? (
                  <button
                    className="action-button buy"
                    onClick={() => {
                      navigate(`/video/${previewPopup.offering!._id}`);
                      closePreviewPopup();
                    }}
                  >
                    <Video size={18} />
                    Watch Now
                  </button>
                ) : (
                  <button
                    className={`action-button ${previewPopup.offering && getOfferingStatus(previewPopup.offering) === "Sold out" ? "sold-out" : "buy"}`}
                    onClick={() => {
                      if (previewPopup.offering && getOfferingStatus(previewPopup.offering) !== "Sold out") {
                        handleBuyNowClick(previewPopup.offering!);
                        closePreviewPopup();
                      }
                    }}
                    disabled={previewPopup.offering ? getOfferingStatus(previewPopup.offering) === "Sold out" : false}
                  >
                    <ShoppingCart size={18} />
                    {previewPopup.offering && getOfferingStatus(previewPopup.offering) === "Sold out"
                      ? "Sold Out"
                      : purchasedOfferings.has(previewPopup.offering?._id || '')
                        ? "Already Purchased"
                        : `Buy ${previewPopup.offering?.type === 'package' ? 'Package' : 'Course'}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {iframeModal.isOpen && iframeModal.url && (
          <div className="popup-overlay">
            <div className="popup-container iframe-modal" style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
              <button 
                onClick={closeIframeModal}
                className="popup-close"
              >
                <X size={24} />
              </button>

              <div style={{ width: '100%', height: '80vh' }}>
                <iframe
                  src={iframeModal.url}
                  title="Course Page"
                  style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Purchase Form Popup */}
        {purchaseForm.isOpen && purchaseForm.offering && (
          <div className="popup-overlay">
            <div className="popup-container purchase-form">
              <button 
                onClick={closePurchaseForm}
                className="popup-close"
              >
                <X size={24} />
              </button>
              
              <div className="popup-header">
                <h3 className="popup-title">
                  Purchase {purchaseForm.offering.type === 'package' ? 'Package' : 'Course'}
                </h3>
              </div>
              
              <p className="popup-description">
                Please fill in your details to purchase "{purchaseForm.offering.title}"
              </p>
              
              <form onSubmit={handleFormSubmit} className="purchase-form-content">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <UserIcon size={18} className="form-icon" />
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={purchaseForm.formData.name.split(' ')[0] || ''}
                      onChange={(e) => {
                        const lastName = purchaseForm.formData.name.split(' ').slice(1).join(' ') || '';
                        handleFormChange({
                          target: {
                            name: 'name',
                            value: `${e.target.value} ${lastName}`.trim()
                          }
                        } as React.ChangeEvent<HTMLInputElement>);
                      }}
                      className="form-input"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      <UserIcon size={18} className="form-icon" />
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={purchaseForm.formData.name.split(' ').slice(1).join(' ') || ''}
                      onChange={(e) => {
                        const firstName = purchaseForm.formData.name.split(' ')[0] || '';
                        handleFormChange({
                          target: {
                            name: 'name',
                            value: `${firstName} ${e.target.value}`.trim()
                          }
                        } as React.ChangeEvent<HTMLInputElement>);
                      }}
                      className="form-input"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <Mail size={18} className="form-icon" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={purchaseForm.formData.email}
                    onChange={handleFormChange}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <Phone size={18} className="form-icon" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={purchaseForm.formData.phone}
                    onChange={handleFormChange}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button"
                    className="cancel-button"
                    onClick={closePurchaseForm}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="action-button buy"
                  >
                    <ShoppingCart size={18} />
                    Confirm Purchase
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </UserDashboardLayout>
  );
};

export default EnhancedUserOfferings;